/**
 * School-shaped licences on price tiers: a single classroom, a team of
 * teachers at a discount on buying separately, or a whole school.
 *
 * A tier MAY carry one (`licence` and, for a team, `licenceSeats` on the tier
 * in `priceTiers`). The product page names it under the tier, and the buyer's
 * library offers a printable licence certificate, because a school asks for
 * proof of what it is allowed to use. A tier without one is an ordinary tier,
 * as every tier was before 2026-10-22.
 *
 * WHAT THE BUYER BOUGHT IS STAMPED AT FULFILMENT, AND ONLY THAT COUNTS. The
 * checkout stages the licence it quoted and fulfilment copies it onto the
 * order line (tierLicence, tierLicenceSeats; fulfilledTierRecord in
 * functions/src/lib/price-tiers.ts, with functions/src/lib/tier-licences.ts
 * mirroring this file's constants). A line without one has no licence:
 * nothing reads the tier's current licence for a purchase, so a licence the
 * seller adds or changes later never reaches an earlier buyer, either way.
 *
 * The terms are the standard wording for each kind, the same for every
 * seller, so a school reading two certificates reads the same promise.
 */

export const TIER_LICENCES = ['single', 'team', 'school'] as const;

export type TierLicenceKind = (typeof TIER_LICENCES)[number];

export type TierLicence = { kind: TierLicenceKind; seats: number | null };

/** A team licence covers this many teachers at most and at least. */
export const TEAM_SEATS_MIN = 2;
export const TEAM_SEATS_MAX = 100;
export const TEAM_SEATS_DEFAULT = 5;

export function isTierLicenceKind(value: unknown): value is TierLicenceKind {
  return typeof value === 'string' && (TIER_LICENCES as readonly string[]).includes(value);
}

/** A team's seat count from a stored or typed value, or null when it is not a whole number in bounds. */
export function teamSeatsOf(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isInteger(n) && n >= TEAM_SEATS_MIN && n <= TEAM_SEATS_MAX ? n : null;
}

/**
 * A tier's licence, or null for none. A team without a usable seat count is
 * no licence at all, rather than a team of some guessed size: the certificate
 * states a number, so it must be the seller's.
 */
export function tierLicenceOf(tier: { licence?: unknown; licenceSeats?: unknown } | null | undefined): TierLicence | null {
  const kind = tier?.licence;
  if (!isTierLicenceKind(kind)) return null;
  if (kind !== 'team') return { kind, seats: null };
  const seats = teamSeatsOf(tier?.licenceSeats);
  return seats === null ? null : { kind, seats };
}

/** The licence as stamped on an order line or an ownership record (tierLicence, tierLicenceSeats). */
export function stampedLicenceOf(record: { tierLicence?: unknown; tierLicenceSeats?: unknown } | null | undefined): TierLicence | null {
  return tierLicenceOf({ licence: record?.tierLicence, licenceSeats: record?.tierLicenceSeats });
}

/**
 * The fields a tier's licence adds to the stored tier: none for a tier
 * without one, so the editors never write an undefined Firestore refuses.
 */
export function tierLicenceFields(tier: { licence?: unknown; licenceSeats?: unknown }): { licence?: TierLicenceKind; licenceSeats?: number } {
  const licence = tierLicenceOf(tier);
  if (!licence) return {};
  return licence.seats === null ? { licence: licence.kind } : { licence: licence.kind, licenceSeats: licence.seats };
}

/** The licence's name: "Whole-school licence". */
export function licenceName(licence: TierLicence): string {
  switch (licence.kind) {
    case 'single':
      return 'Single classroom licence';
    case 'team':
      return `Team licence for ${licence.seats} teachers`;
    case 'school':
      return 'Whole-school licence';
  }
}

/** One line under a tier on the product page. */
export function licenceSummary(licence: TierLicence): string {
  switch (licence.kind) {
    case 'single':
      return 'For one teacher to use with their own classes.';
    case 'team':
      return `For up to ${licence.seats} teachers at one school, each with their own classes.`;
    case 'school':
      return 'For every teacher at one school site.';
  }
}

/** Who may use it, as the certificate's first term. */
function whoMayUse(licence: TierLicence): string {
  switch (licence.kind) {
    case 'single':
      return 'One teacher may use this resource with the classes and students they teach.';
    case 'team':
      return `Up to ${licence.seats} teachers at one school may each use this resource with the classes and students they teach.`;
    case 'school':
      return 'Every teacher at one school site may use this resource with the classes and students they teach.';
  }
}

/** Who it may not go to, as the certificate's last term. */
function whoMayNot(licence: TierLicence): string {
  switch (licence.kind) {
    case 'single':
      return 'It may not be shared with other teachers, posted publicly or resold.';
    case 'team':
      return `It may not be shared beyond those ${licence.seats} teachers, posted publicly or resold.`;
    case 'school':
      return 'It may not be shared with other schools or sites, posted publicly or resold.';
  }
}

/** The licence's terms, as the certificate lists them. */
export function licenceTerms(licence: TierLicence): string[] {
  return [
    whoMayUse(licence),
    'Licensed teachers may print, copy and project it for those students, and share it with them through the school’s own password-protected online classroom.',
    whoMayNot(licence),
    'The licence does not expire and does not transfer the seller’s copyright.',
  ];
}

/**
 * What an owned product's licence is: the one stamped on the order line that
 * granted it, or null. Never the licence its tier carries now (see the
 * header): the line is the record of the sale.
 */
export function licenceForPurchase(line: { tierLicence?: unknown; tierLicenceSeats?: unknown } | null | undefined): TierLicence | null {
  return stampedLicenceOf(line);
}

/**
 * The three tiers the listing form adds in one go for a teaching resource:
 * one teacher, a team at a discount, a whole school. Prices are the seller's
 * to set; the single classroom starts at the product's current price, and
 * the others start empty so the price check asks for them.
 */
export function schoolLicenceTiers(startId: number, singlePrice: string): Array<{
  id: number;
  name: string;
  price: string;
  description: string;
  purchaseType: 'one-off';
  licence: TierLicenceKind;
  licenceSeats?: number;
}> {
  return [
    {
      id: startId,
      name: 'Single classroom',
      price: singlePrice,
      description: '<p>For one teacher and their classes.</p>',
      purchaseType: 'one-off',
      licence: 'single',
    },
    {
      id: startId + 1,
      name: `Team of ${TEAM_SEATS_DEFAULT} teachers`,
      price: '',
      description: `<p>For up to ${TEAM_SEATS_DEFAULT} teachers at one school, for less than buying separately.</p>`,
      purchaseType: 'one-off',
      licence: 'team',
      licenceSeats: TEAM_SEATS_DEFAULT,
    },
    {
      id: startId + 2,
      name: 'Whole school',
      price: '',
      description: '<p>For every teacher at one school site.</p>',
      purchaseType: 'one-off',
      licence: 'school',
    },
  ];
}

/**
 * The order line that granted a product, from the buyer's orders newest
 * first: the line for `tierId` when one is given (the tier the ownership
 * record names, which a subscriber's tier switch can move on from the line
 * that first granted it), else the newest line for the product.
 */
export function purchaseLineFor<L extends { productId?: unknown; tierId?: unknown }>(
  orders: ReadonlyArray<{ id: string; items?: readonly L[] | null }>,
  productId: string,
  tierId?: unknown,
): { orderId: string; line: L } | null {
  const anyTier = tierId === undefined || tierId === null || tierId === '';
  for (const order of orders) {
    for (const line of order.items ?? []) {
      if (line?.productId !== productId) continue;
      if (anyTier || String(line.tierId) === String(tierId)) return { orderId: order.id, line };
    }
  }
  return null;
}

type SchoolLicenceTier = ReturnType<typeof schoolLicenceTiers>[number];

/**
 * The tier list the "Add school licences" button leaves: the three presets
 * for a product not priced in tiers yet (the single classroom at its current
 * price), else the existing tiers plus each preset whose kind none of them
 * carries, while there is room. Never removes or changes a seller's tier.
 * Each new tier unlocks every file, since the licences differ in who may use
 * the files, not in which files they are (both forms read a tier with no file
 * record as unlocking all of them).
 */
export function withSchoolLicenceTiers<T extends { id: number; licence?: unknown; licenceSeats?: unknown }>(
  tiers: readonly T[],
  alreadyTiered: boolean,
  singlePrice: string,
  startId: number,
  maxTiers: number,
): Array<T | SchoolLicenceTier> {
  const presets = schoolLicenceTiers(startId, singlePrice);
  if (!alreadyTiered) return presets;
  const held = new Set(tiers.map((tier) => tierLicenceOf(tier)?.kind ?? (tier.licence === 'team' ? 'team' : null)));
  const out: Array<T | SchoolLicenceTier> = [...tiers];
  for (const preset of presets) {
    if (out.length >= maxTiers) break;
    if (!held.has(preset.licence)) out.push(preset);
  }
  return out;
}

/** The certificate's reference: the product and the order, so the seller and support can find the sale. */
export function licenceReference(productId: string, orderId: string): string {
  const order = orderId.replace(/^pi_/, '').slice(-8).toUpperCase();
  return `${productId.slice(0, 8).toUpperCase()}-${order}`;
}
